// Чтение локализованного текста курса: перевод на язык ученика → оригинал.
//
// ЕДИНСТВЕННЫЙ хелпер на всё приложение. Размазать «взять перевод, если есть» по
// местам чтения (дерево курса, тема, календарь, поиск, офлайн-сборка) — известный
// класс дефектов этого проекта: одна поверхность начинает показывать перевод,
// другая оригинал, и пользователь видит два разных названия одной темы на
// соседних экранах.
//
// Живёт в `_shared`, потому что читают его и `courses`, и `study-plan`, и
// офлайн-сборка на клиенте.

/** Карта переводов; `null`/`undefined` — переводов нет. */
export type LocalizedText = Record<string, string> | null | undefined;

/**
 * Текст на языке отображения с фолбэком на оригинал.
 *
 * Цепочка ровно двухступенчатая и это осознанно: перевод на язык ученика →
 * оригинал автора. Третьей ступени «а если нет, то по-английски» здесь нет —
 * английский для ученика такой же чужой язык, как язык автора, и подмена одного
 * непонятного текста другим ничего не улучшает (в отличие от каталога, где
 * английский работает как лингва франка при ПОИСКЕ).
 *
 * @param original Оригинальная строка из колонки `title` / `description`.
 * @param localized jsonb-карта переводов.
 * @param displayLocale Язык, на котором ученик читает курс. `null` = оригинал.
 */
export function pickCourseLocalized<T extends string | null>(
	original: T,
	localized: LocalizedText,
	displayLocale: string | null | undefined,
): T {
	if (!displayLocale || !localized) return original;
	const translated = localized[displayLocale]?.trim();
	// Пустая строка в карте = перевода нет. Вернуть её значило бы показать
	// безымянную тему вместо названия на языке автора.
	return translated ? (translated as T) : original;
}

/**
 * Разбор jsonb, приехавшего из SQLite строкой.
 *
 * PowerSync отдаёт jsonb-колонки как текст, поэтому офлайн-сборка обязана его
 * распарсить. Невалидный JSON (обрезанная строка, дрейф схемы) не должен ронять
 * весь экран курса — возвращаем `null` и читаем оригинал.
 */
export function parseLocalizedColumn(raw: string | null | undefined): LocalizedText {
	if (!raw) return null;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
		const out: Record<string, string> = {};
		for (const [locale, value] of Object.entries(parsed as Record<string, unknown>)) {
			if (typeof value === "string" && value.trim().length > 0) out[locale] = value;
		}
		return out;
	} catch {
		return null;
	}
}
